import React, {useEffect, useRef, useState, useCallback, useMemo} from 'react';
import {
  useDataChannel,
  useRemoteParticipants,
  useSpeakingParticipants,
  useLocalParticipant,
  useTracks,
} from '@livekit/components-react';
import {RoomEvent, Track} from 'livekit-client';
import {TranscriptionTile} from './TranscriptionTile';
import {useSelector} from 'react-redux';
import LiveKitTextInput from './LiveKitTextInput';
import LiveKitTranscriptPanel from './LiveKitTranscriptPanel';

const MIC_PERMISSION_DENIED_PATTERNS = [
  'notallowederror',
  'permissiondenied',
  'denied permission',
  'permission denied',
  'microphone permission',
  'securityerror',
];

const isMicrophonePermissionDeniedError = error => {
  const details = [
    error?.name,
    error?.message,
    error?.cause?.message,
    String(error || ''),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return MIC_PERMISSION_DENIED_PATTERNS.some(pattern => details.includes(pattern));
};

export default function LiveKitSDK({micPermissionDenied = false}) {
  const tracks = useTracks();
  const participants = useRemoteParticipants({
    updateOnlyOn: [RoomEvent.ParticipantMetadataChanged],
  });
  const agentParticipant = participants.find(p => p.isAgent);
  const {
    localParticipant,
    isMicrophoneEnabled,
    microphoneTrack,
    lastMicrophoneError,
  } = useLocalParticipant();
  const speakingParticipants = useSpeakingParticipants();

  const oneToOneMeetingStatus = useSelector(
    state => state.communication.speechToTextService.oneToOneMeetingStatus,
  );

  // Guardrail: ensure mic is enabled in 1:1 tutor mode.
  // If the mic gets disabled (permissions reset, device switch, etc) the agent won't hear the user,
  // which matches the "tutor asks first question then never responds" symptom.
  useEffect(() => {
    if (!oneToOneMeetingStatus) return;
    if (!localParticipant) return;
    if (isMicrophoneEnabled) return;
    Promise.resolve(localParticipant.setMicrophoneEnabled(true)).catch(e => {
      console.warn('[LiveKit] Failed to enable microphone', e);
    });
  }, [oneToOneMeetingStatus, localParticipant, isMicrophoneEnabled]);

  useEffect(() => {
    if (!oneToOneMeetingStatus) return;
    if (lastMicrophoneError) {
      console.warn('[LiveKit] lastMicrophoneError', lastMicrophoneError);
    }
  }, [oneToOneMeetingStatus, lastMicrophoneError]);

  const micPermissionDeniedFromLiveKit = useMemo(
    () => isMicrophonePermissionDeniedError(lastMicrophoneError),
    [lastMicrophoneError],
  );
  const showMicPermissionWarning =
    oneToOneMeetingStatus &&
    !isMicrophoneEnabled &&
    (micPermissionDenied || micPermissionDeniedFromLiveKit);

  useEffect(() => {
    if (!oneToOneMeetingStatus) return;
    if (!microphoneTrack) {
      console.warn('[LiveKit] microphoneTrack publication is missing (not published yet?)');
      return;
    }
    console.log('[LiveKit] microphoneTrack', {
      sid: microphoneTrack.trackSid,
      muted: microphoneTrack.isMuted,
      kind: microphoneTrack.kind,
      source: microphoneTrack.source,
      hasTrack: Boolean(microphoneTrack.track),
    });
  }, [oneToOneMeetingStatus, microphoneTrack]);

  // who’s speaking right now?
  const isAgentSpeaking = agentParticipant
    ? speakingParticipants.some(p => p.identity === agentParticipant.identity)
    : false;
  const isUserSpeaking = speakingParticipants.some(
    p => p.identity === localParticipant.identity,
  );

  // debounce window (ms) before we declare “agent finished”
  const DEBOUNCE_MS = 2000;
  const prevAgentRef = useRef(false);
  const agentTimeout = useRef();

  // overlay state: show whenever agent finishes, hide on user speak
  const [enableInput, setEnableInput] = useState(false);

  // React-side transcript (Unity rendering disabled)
  const [transcriptSourceKey, setTranscriptSourceKey] = useState('');
  const [transcriptSourceText, setTranscriptSourceText] = useState('');

  // Reset overlay + agent state when (re)connecting tutor mode so
  // stale speaking state doesn't auto-enable input immediately.
  useEffect(() => {
    clearTimeout(agentTimeout.current);
    prevAgentRef.current = false;
    setEnableInput(false);
    setTranscriptSourceKey('');
    setTranscriptSourceText('');
  }, [oneToOneMeetingStatus]);

  // detect agent start/finish
  useEffect(() => {
    if (isAgentSpeaking && !prevAgentRef.current) {
      // agent just started
      prevAgentRef.current = true;
      clearTimeout(agentTimeout.current);
    }
    if (!isAgentSpeaking && prevAgentRef.current) {
      // agent just stopped → debounce before showing overlay
      clearTimeout(agentTimeout.current);
      agentTimeout.current = setTimeout(() => {
        if (!isAgentSpeaking) {
          if (oneToOneMeetingStatus) {
            setEnableInput(true);
          }
        }
      }, DEBOUNCE_MS);
      prevAgentRef.current = false;
    }
    return () => clearTimeout(agentTimeout.current);
  }, [isAgentSpeaking]);

  // hide overlay as soon as user speaks
  useEffect(() => {
    if (isUserSpeaking || isAgentSpeaking || !oneToOneMeetingStatus) {
      setEnableInput(false);
    }
  }, [isUserSpeaking, isAgentSpeaking, oneToOneMeetingStatus]);

  // cleanup on unmount
  useEffect(() => {
    return () => clearTimeout(agentTimeout.current);
  }, []);

  // — your transcription + data‐channel logic unchanged —
  const aat = tracks.find(
    t => t.publication.kind === Track.Kind.Audio && t.participant.isAgent,
  );
  let agentAudioTrack;
  if (aat) agentAudioTrack = aat;
  else if (agentParticipant) {
    agentAudioTrack = {
      participant: agentParticipant,
      source: Track.Source.Microphone,
    };
  }

  const chatTileContent = useMemo(() => {
    if (!agentAudioTrack) return null;
    return (
      <TranscriptionTile
        agentAudioTrack={agentAudioTrack}
        accentColor="#FF0000"
        onTranscript={({id, text, final, participant}) => {
          // Single-line replacement: show whichever side spoke most recently.
          setTranscriptSourceKey(`${participant}:${id}`);
          setTranscriptSourceText(text || '');
        }}
      />
    );
  }, [agentAudioTrack]);

  const onDataReceived = useCallback(msg => {
    console.log('onDataReceived', msg);
  }, []);
  useDataChannel(e => console.log('useDataChannel', e));

  return (
    <>
      {chatTileContent}
      {oneToOneMeetingStatus && (
        <LiveKitTranscriptPanel
          sourceKey={transcriptSourceKey}
          sourceText={transcriptSourceText}
        />
      )}
      {oneToOneMeetingStatus &&
      <LiveKitTextInput
        disabled={!enableInput}
        showPromptTab={enableInput && !showMicPermissionWarning}
        showMicPermissionWarning={showMicPermissionWarning}
        onSent={() => setEnableInput(false)}
        onMessageSent={(msg) => {
          // Reflect typed message in transcript UI (non-stacking).
          setTranscriptSourceKey(`player:typed_${Date.now()}`);
          setTranscriptSourceText(msg || '');
        }}
      />}
    </>
  );
}
